import { TrackingEvent } from '../types/tracking'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget =
  | 'download_shipping_label'
  | 'confirm_everything_is_ok'
  | 'view_delivery_instructions'
  | 'track_parcel'
  | 'mark_as_received'
  | 'copy_tracking_number'
  | 'open_tracking_page'
  | 'tracking_information_link'
  | 'close_page'
  | 'get_shipping_label'
  | 'confirm'
  | 'hyperlink'
  | 'select_dropoff_type'
  | 'download_digital_label'
  | 'open_digital_label'
  | 'delete_contact_details'
  | 'confirm_phone_number_deletion'
  | 'contact_details_continue'
  | 'seller_add_contact_details'
  | 'select_label_type'
  | 'mark_transaction_as_shipped'
  | 'confirm_mark_transaction_as_shipped'
  | 'return_order'
  | 'see_drop_off_points'
  | 'drop_off_point'
  | 'drop_off_point_details'
  | 'address_search_bar'
  | 'drop_off_points_map_link'
  | 'link_dropoff_map'
  | 'extend_shipping_deadline'
  | 'cancel_shipping_deadline_extension'
  | 'agree_extend_shipping_deadline'
  | 'decline_shipping_deadline_extension'
  | 'cancel_carrier_change_request'
  | 'accept_carrier_change_request'
  | 'decline_carrier_change_request'

export type ShippingInstructionsTimeOnTaskEventArgs = {
  screen: string
  duration: number
  shipmentId: number | undefined
}

export type ShippingInstructionsTimeOnTaskEventExtra = {
  screen: string
  duration: number
  shipment_id: number | undefined
}

export const shippingInstructionsTimeOnTaskEvent = (
  args: ShippingInstructionsTimeOnTaskEventArgs,
) => {
  const extra: ShippingInstructionsTimeOnTaskEventExtra = {
    screen: args.screen,
    duration: args.duration,
    shipment_id: args.shipmentId,
  }

  return {
    event: 'shipping_instructions.time_in_shipping_instructions',
    extra,
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/user/view.avdl#L25
type ViewEventTarget =
  | 'label_type_selection'
  | 'drop_off_point'
  | 'see_drop_off_points'
  | 'extend_shipping_deadline'

type ViewEventArgs = {
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewEventTarget
  target_details?: string
  screen?: string
}

export const viewEvent = (args: ViewEventArgs): TrackingEvent => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type CheckoutBuyerInputEventArgs = {
  transactionId?: number | null
  targetDetails?: string
  screen: string
  target: string
  valid: boolean | null
}

type CheckoutBuyerInputEventExtra = {
  valid: boolean | null
  screen: string
  target: string
  target_details?: string
  transaction_id?: number
}

export const checkoutBuyerInputEvent = (args: CheckoutBuyerInputEventArgs): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: CheckoutBuyerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'checkout.buyer.input',
    extra,
  }
}

type TransactionSellerInputEventArgs = {
  screen: string
  target: string
  valid: boolean | null
  targetDetails?: string
  transactionId?: number | null
}

type TransactionSellerInputEventExtra = {
  screen: string
  target: string
  valid: boolean | null
  target_details?: string
  transaction_id?: number
}

export const transactionSellerInputEvent = (
  args: TransactionSellerInputEventArgs,
): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: TransactionSellerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'transaction.seller_input',
    extra,
  }
}

type SelfServiceViewScreenEventArgs = {
  screen: string
  transactionId: number | string
  faqEntryId?: number | string | null
  details?: string | null
  variant?: string | null
}

type SelfServiceViewScreenEventExtra = {
  screen: string
  transaction_id: number | string
  faq_entry_id?: number | string | null
  details?: string | null
  // "variant" is not present in the schema, however it is used in some places
  variant?: string | null
}

export const selfServiceViewScreenEvent = (args: SelfServiceViewScreenEventArgs): TrackingEvent => {
  const { screen, transactionId, faqEntryId, details, variant } = args

  const extra: SelfServiceViewScreenEventExtra = {
    screen,
    transaction_id: transactionId,
  }

  if (faqEntryId) extra.faq_entry_id = faqEntryId
  if (details) extra.details = details
  if (variant) extra.variant = variant

  return {
    event: 'user.self_service.view_screen',
    extra,
  }
}

type SettingsUserInputEventArgs = {
  screen: string
  target: string
  isValid: boolean
  targetDetails?: string
}

type SettingsUserInputEventExtra = {
  screen: string
  target: string
  valid: boolean
  target_details?: string
}

export const settingsUserInputEvent = (args: SettingsUserInputEventArgs): TrackingEvent => {
  const { screen, target, isValid, targetDetails } = args

  const extra: SettingsUserInputEventExtra = {
    screen,
    target,
    valid: isValid,
    target_details: targetDetails,
  }

  return {
    event: 'settings.user.input',
    extra,
  }
}

type BuyerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type BuyerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

export const buyerViewAddContactDetailsEvent = (args: BuyerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: BuyerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'buyer.view_add_contact_details',
    extra,
  }
}

type SellerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type SellerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

export const sellerViewAddContactDetailsEvent = (args: SellerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: SellerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'seller.view_add_contact_details',
    extra,
  }
}
